/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Parameter Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Services
import medicalParameterService from '@nephro/services/medical-parameter-service';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Constants
import {
  CREATE_MEDICAL_PARAMETER,
  DELETE_MEDICAL_PARAMETER_BY_ID,
  GET_ALL_MEDICAL_PARAMETERS,
  GET_MEDICAL_PARAMETER_BY_ID,
  UPDATE_MEDICAL_PARAMETER
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get medical parameters from backend
 * @param { object } action - Action with payload data
 */
export function* getMedicalParametersArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(
      medicalParameterService.getMedicalParameters,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get medical parameter info by id
 * @param { object } action - Action with payload data
 */
export function* getMedicalParameterById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      medicalParameterService.getMedicalParameterById,
      id
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.parameterNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to create medical parameter
 * @param { object } action - Action with payload data
 */
export function* createMedicalParameter(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const {
    param_key: medical_key,
    param_value: medical_value,
    param_description: medical_description
  } = data;

  try {
    const response = yield call(
      medicalParameterService.createMedicalParameter,
      medical_key,
      medical_value,
      medical_description
    );
    yield put(
      setResponse(
        'success',
        'new medical parameter',
        'paramMg.parameterCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update medical parameter
 * @param { object } action - Action with payload data
 */
export function* updateMedicalParameter(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const {
    param_key: medical_key,
    param_value: medical_value,
    param_description: medical_description,
    id: medical_id
  } = data;

  try {
    const response = yield call(
      medicalParameterService.updateMedicalParameter,
      medical_key,
      medical_value,
      medical_description,
      medical_id
    );
    yield put(
      setResponse(
        'success',
        'updated medical parameter',
        'paramMg.parameterUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete medical parameter info by id
 * @param { object } action - Action with payload data
 */
export function* deleteMedicalParameterById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      medicalParameterService.deleteMedicalParameterById,
      id
    );
    yield put(
      setResponse(
        'success',
        'deleted medical parameter',
        'paramMg.parameterDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchMedicalParameterSaga() {
  yield takeLatest(GET_ALL_MEDICAL_PARAMETERS, getMedicalParametersArray);
  yield takeLatest(GET_MEDICAL_PARAMETER_BY_ID, getMedicalParameterById);
  yield takeLatest(CREATE_MEDICAL_PARAMETER, createMedicalParameter);
  yield takeLatest(UPDATE_MEDICAL_PARAMETER, updateMedicalParameter);
  yield takeLatest(DELETE_MEDICAL_PARAMETER_BY_ID, deleteMedicalParameterById);
}
